import { useState, useEffect } from 'react';
import { api } from '../../utils/api';
import AdminLayout from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Edit, Trash2, UserPlus, Wallet, Eye, MoreVertical, Search } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { StatusBadge } from '../../components/admin/StatusBadge';
import { UserEditDialog } from '../../components/admin/UserEditDialog';
import { UserCreateDialog } from '../../components/admin/UserCreateDialog';
import { BalanceDialog } from '../../components/admin/BalanceDialog';
import { UserDetailDialog } from '../../components/admin/UserDetailDialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Input } from '../../components/ui/input';

export default function AdminUsersPage() {
  const { t } = useLanguage();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [addDepositOpen, setAddDepositOpen] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositNote, setDepositNote] = useState('');
  const [depositMethod, setDepositMethod] = useState('bank_transfer');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [balanceDialogOpen, setBalanceDialogOpen] = useState(false);
  const [balanceUser, setBalanceUser] = useState(null);
  const [balanceAmount, setBalanceAmount] = useState('');
  const [balanceNote, setBalanceNote] = useState('');
  const [balanceAction, setBalanceAction] = useState('add');
  const [formData, setFormData] = useState({
    full_name: '', email: '', phone: '', kyc_status: 'pending',
    is_active: true, role: 'user', account_status: 'active', password: ''
  });
  const [createFormData, setCreateFormData] = useState({
    full_name: '', email: '', password: '', phone: '', balance: 0, role: 'user'
  });

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.getAdminUsers();
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (u.full_name || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q);
  });

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      full_name: user.full_name, email: user.email, phone: user.phone || '',
      kyc_status: user.kyc_status, is_active: user.is_active, role: user.role || 'user',
      account_status: user.account_status || 'active', password: '',
      transfer_limit: user.transfer_limit || ''
    });
    setDialogOpen(true);
  };

  const openBalanceDialog = (user) => {
    setBalanceUser(user);
    setBalanceAmount(''); setBalanceNote(''); setBalanceAction('add');
    setBalanceDialogOpen(true);
  };

  const openDetailDialog = async (user) => {
    setDetailLoading(true); setDetailDialogOpen(true);
    try {
      const res = await api.getAdminUserDetail(user.id);
      setDetailData(res.data);
    } catch {
      toast.error(t('admin_users_update_failed'));
      setDetailDialogOpen(false);
    } finally { setDetailLoading(false); }
  };

  const refreshDetailData = async (userId) => {
    try {
      const res = await api.getAdminUserDetail(userId);
      setDetailData(res.data);
      fetchUsers();
    } catch (error) { console.error('Failed to refresh detail:', error); }
  };

  const handleConfirmDeposit = async (depositId) => {
    try {
      await api.confirmDeposit(depositId, 'confirm');
      toast.success(t('admin_deposits_confirmed'));
      refreshDetailData(detailData.user.id);
    } catch { toast.error(t('admin_deposits_confirm_failed')); }
  };

  const handleRejectDeposit = async (depositId) => {
    if (!window.confirm(t('admin_deposits_reject_confirm'))) return;
    try {
      await api.confirmDeposit(depositId, 'reject');
      toast.success(t('admin_deposits_rejected'));
      refreshDetailData(detailData.user.id);
    } catch { toast.error(t('admin_deposits_reject_failed')); }
  };

  const handleDeleteDeposit = async (depositId) => {
    if (!window.confirm(t('admin_deposits_reject_confirm'))) return;
    try {
      await api.deleteDeposit(depositId);
      toast.success(t('admin_deposits_deleted'));
      refreshDetailData(detailData.user.id);
    } catch { toast.error(t('admin_deposits_delete_failed')); }
  };

  const handleAddDeposit = async () => {
    const amt = parseFloat(depositAmount);
    if (!amt || amt <= 0) { toast.error(t('admin_users_deposit_failed')); return; }
    try {
      await api.createAdminDeposit(detailData.user.id, { amount: amt, method: depositMethod, note: depositNote });
      toast.success(t('admin_users_deposit_added'));
      setAddDepositOpen(false); setDepositAmount(''); setDepositNote('');
      refreshDetailData(detailData.user.id);
    } catch { toast.error(t('admin_users_deposit_failed')); }
  };

  const handleUpdate = async () => {
    try {
      const data = { ...formData };
      if (!data.password) delete data.password;
      data.transfer_limit = data.transfer_limit ? parseFloat(data.transfer_limit) : null;
      await api.updateUser(editingUser.id, data);
      toast.success(t('admin_users_updated')); setDialogOpen(false); fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || t('admin_users_update_failed')); }
  };

  const handleCreate = async () => {
    if (!createFormData.full_name || !createFormData.email || !createFormData.password) {
      toast.error(t('admin_users_create_failed')); return;
    }
    try {
      await api.createAdminUser(createFormData);
      toast.success(t('admin_users_created')); setCreateDialogOpen(false);
      setCreateFormData({ full_name: '', email: '', password: '', phone: '', balance: 0, role: 'user' });
      fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || t('admin_users_create_failed')); }
  };

  const handleBalanceAdjust = async () => {
    const amt = parseFloat(balanceAmount);
    if (!amt || amt <= 0) { toast.error(t('admin_users_balance_failed')); return; }
    const finalAmount = balanceAction === 'subtract' ? -amt : amt;
    try {
      await api.adjustUserBalance(balanceUser.id, { amount: finalAmount, note: balanceNote });
      toast.success(t('admin_users_balance_updated')); setBalanceDialogOpen(false); fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || t('admin_users_balance_failed')); }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm(t('admin_users_delete_confirm'))) return;
    try {
      await api.deleteUser(userId);
      toast.success(t('admin_users_deleted')); fetchUsers();
    } catch (error) { toast.error(error.response?.data?.detail || t('admin_users_delete_failed')); }
  };

  return (
    <AdminLayout>
      <div data-testid="admin-users-page">
        <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold text-primary mb-1">{t('admin_users_title')}</h1>
            <p className="text-muted-foreground text-sm">{t('admin_users_subtitle')}</p>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} className="gap-2" data-testid="create-user-button">
            <UserPlus className="h-4 w-4" />{t('admin_users_create')}
          </Button>
        </div>

        <div className="mb-4 relative max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('admin_users_search') || '検索...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="user-search-input"
          />
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">{t('loading')}</div>
        ) : (
          <div className="bg-card border border-border/50 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-secondary/50">
                <tr>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">{t('name')}</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground hidden md:table-cell">{t('email')}</th>
                  <th className="text-right px-4 py-3 text-sm font-medium text-muted-foreground">{t('balance')}</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground hidden lg:table-cell">KYC</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground hidden sm:table-cell">{t('admin_users_role')}</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground hidden lg:table-cell">{t('admin_users_status')}</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-muted-foreground w-16">{t('admin_users_actions') || '操作'}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-secondary/30 smooth-transition" data-testid={`user-row-${user.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-medium text-primary text-sm">{user.full_name}</div>
                      <div className="text-xs text-muted-foreground md:hidden">{user.email}</div>
                    </td>
                    <td className="px-4 py-3 text-sm text-muted-foreground hidden md:table-cell">{user.email}</td>
                    <td className="px-4 py-3 text-right">
                      <span className="font-mono text-sm font-medium text-primary" data-testid={`balance-${user.id}`}>
                        ¥{(user.balance || 0).toLocaleString()}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center hidden lg:table-cell"><StatusBadge status={user.kyc_status} type="kyc" /></td>
                    <td className="px-4 py-3 text-center hidden sm:table-cell"><StatusBadge status={user.role || 'user'} type="role" /></td>
                    <td className="px-4 py-3 text-center hidden lg:table-cell"><StatusBadge status={user.account_status || 'active'} type="account" /></td>
                    <td className="px-4 py-3 text-center">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`user-actions-${user.id}`}>
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openDetailDialog(user)} data-testid={`detail-user-${user.id}`}>
                            <Eye className="h-4 w-4 mr-2" />{t('admin_users_details_btn') || '詳細'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEditDialog(user)} data-testid={`edit-user-${user.id}`}>
                            <Edit className="h-4 w-4 mr-2" />{t('admin_users_edit_btn') || '編集'}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openBalanceDialog(user)} data-testid={`balance-user-${user.id}`}>
                            <Wallet className="h-4 w-4 mr-2" />{t('admin_users_balance_manage') || '残高管理'}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => handleDelete(user.id)} className="text-destructive focus:text-destructive" data-testid={`delete-user-${user.id}`}>
                            <Trash2 className="h-4 w-4 mr-2" />{t('admin_users_delete_btn') || '削除'}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
                {filteredUsers.length === 0 && (
                  <tr><td colSpan="7" className="px-6 py-12 text-center text-muted-foreground">{t('admin_data_not_found')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        <UserEditDialog open={dialogOpen} onOpenChange={setDialogOpen} formData={formData} setFormData={setFormData} onSubmit={handleUpdate} />
        <UserCreateDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} formData={createFormData} setFormData={setCreateFormData} onSubmit={handleCreate} />
        <BalanceDialog open={balanceDialogOpen} onOpenChange={setBalanceDialogOpen} user={balanceUser} amount={balanceAmount} setAmount={setBalanceAmount} note={balanceNote} setNote={setBalanceNote} action={balanceAction} setAction={setBalanceAction} onSubmit={handleBalanceAdjust} />
        <UserDetailDialog
          open={detailDialogOpen} onOpenChange={setDetailDialogOpen}
          data={detailData} loading={detailLoading}
          addDepositOpen={addDepositOpen} setAddDepositOpen={setAddDepositOpen}
          depositAmount={depositAmount} setDepositAmount={setDepositAmount}
          depositNote={depositNote} setDepositNote={setDepositNote}
          depositMethod={depositMethod} setDepositMethod={setDepositMethod}
          onAddDeposit={handleAddDeposit} onConfirmDeposit={handleConfirmDeposit}
          onRejectDeposit={handleRejectDeposit} onDeleteDeposit={handleDeleteDeposit}
        />
      </div>
    </AdminLayout>
  );
}
